import React, { useEffect, useMemo, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { findIndex, isEmpty } from 'lodash'
import FilterBar from './FilterBar'

const FilterBarContainer = ({
  sections,
  fetchAsyncSections,
  filterName: filterNameProp,
  servicesConfig: servicesConfigProp,
  onChangeOnMount: onChangeOnMountProp,
  ...props
}) => {
  const [filterSections, setFilterSections] = useState(null)
  const [defaultFilterName, setDefaultFilterName] = useState(null)

  const { fetchDefault, ...servicesConfig } = servicesConfigProp

  const handleSections = useCallback(
    async (signal) => {
      if (!isEmpty(sections)) {
        setFilterSections(sections)
        return
      }

      if (!fetchDefault) {
        setFilterSections([])
        return
      }

      try {
        const { name, sections: defaultSections } = await fetchDefault(signal)
        setFilterSections(defaultSections)
        setDefaultFilterName(name ?? 'None')
      } catch (err) {
        setFilterSections([])
      }
    },
    [sections, fetchDefault]
  )

  useEffect(() => {
    const controller = new AbortController()
    handleSections(controller.signal)
    return () => {
      controller.abort()
    }
  }, [sections, fetchDefault])

  const filterName = useMemo(
    () => filterNameProp ?? defaultFilterName,
    [defaultFilterName, filterNameProp]
  )

  // If the filterbar is server-side handled
  // At the first render the onChange is dispatched
  // Hence it returns the filter internal state
  const onChangeOnMount = useMemo(
    () => onChangeOnMountProp || !!fetchDefault,
    [onChangeOnMountProp, fetchDefault]
  )

  const onFetchAsyncSections = useCallback(
    (filterState, asyncSection, asyncDepsSection, sections) => {
      if (fetchAsyncSections) {
        const filters = { ...filterState }

        fetchAsyncSections(filters, asyncSection, asyncDepsSection)
          .then((bodyDepSections) => {
            const depSections = bodyDepSections.sections ?? []

            depSections.forEach((depSection) => {
              const index = findIndex(sections, { id: depSection.id })
              sections.splice(index, 1, depSection)
            })

            setFilterSections(sections)
          })
          .catch(() => setFilterSections([]))
      }
    },
    [fetchAsyncSections]
  )
  return !isEmpty(filterSections) ? (
    <FilterBar
      {...props}
      filterName={filterName}
      sections={filterSections}
      servicesConfig={servicesConfig}
      onChangeOnMount={onChangeOnMount}
      onFetchAsyncSections={onFetchAsyncSections}
    />
  ) : null
}

FilterBarContainer.propTypes = {
  sections: PropTypes.array,
  filterName: PropTypes.string,
  servicesConfig: PropTypes.object
}

FilterBarContainer.defaultProps = {
  filterName: null,
  servicesConfig: {}
}

export default FilterBarContainer
